import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2Icon, PackageSearch, SlidersHorizontal } from "lucide-react";
import {
  useInfiniteGetCollectionByHandleQuery,
  useGetProductTypesQuery,
} from "@modules/shopify/hooks";
import { getCountryVariables } from "@/modules/shared/localization/const/countries";
import { ProductGrid } from "@modules/shop/components/ProductCard";
import { PageBreadcrumb } from "@modules/shared/Breadcrumbs/PageBreadcrumb";
import { keepPreviousData } from "@tanstack/react-query";
import { parseAsString, useQueryState } from "nuqs";
import { resolveCollectionSortOption } from "@modules/shop/components/Products/sort-options";
import { useProductFilters } from "@modules/shop/hooks/useProductFilters";
import { FilterSidebar } from "@modules/shop/components/Products/FilterSidebar";
import { ActiveFilterPills } from "@modules/shop/components/Products/ActiveFilterPills";
import {
  MobileFilterDrawer,
  getActiveFilterCount,
} from "@modules/shop/components/Products/MobileFilterDrawer";
import { Button } from "@ui/components/ui/button";
import { Badge } from "@ui/components/ui/badge";
import { buildProductFilters } from "@modules/shop/components/Products/filter-params";
import type { GetCollectionByHandleQuery } from "@gql/types";
import type { Locale } from "@/modules/shared/localization/const/language";

const OBSERVED_ITEMS_FROM_END = 10;

export const PRODUCTS_PAGE_SIZE = 50;

/** Handle of the Shopify collection that contains all products. */
export const ALL_PRODUCTS_COLLECTION = "svi-proizvodi";

/** Product ID excluded from the storefront listing (heel spur insole). */
export const EXCLUDED_PRODUCT_ID = "15616740622719";

type ProductsPageProps = {
  locale: Locale;
};

export function ProductsPage({ locale }: Readonly<ProductsPageProps>) {
  const { t } = useTranslation();

  // Sort state driven by URL via nuqs
  const [sortKey, setSortKey] = useQueryState(
    "sort",
    parseAsString.withDefault("default").withOptions({
      history: "push",
      shallow: true,
    }),
  );
  const activeSort = resolveCollectionSortOption(sortKey);

  // Filter state driven by URL via nuqs
  const filterState = useProductFilters();
  const { isFiltered, filters } = filterState;

  // Mobile filter drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const activeFilterCount = getActiveFilterCount(filters);

  // Scroll to top when active filters change (skip first render)
  const isFirstFilterRender = useRef(true);
  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    filters.type.join(","),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    filters.color.join(","),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    filters.size.join(","),
    filters.minPrice,
    filters.maxPrice,
    sortKey,
  ]);

  const variables = {
    handle: ALL_PRODUCTS_COLLECTION,
    numberOfProducts: PRODUCTS_PAGE_SIZE,
    productFilters: buildProductFilters(filters),
    sortKey: activeSort.sortKey,
    reverse: activeSort.reverse,
    ...getCountryVariables(locale),
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteGetCollectionByHandleQuery(variables, {
      initialPageParam: { cursor: undefined as string | undefined },
      getNextPageParam: (lastPage: GetCollectionByHandleQuery) => {
        const pageInfo = lastPage.collection?.products.pageInfo;

        if (!pageInfo?.hasNextPage || !pageInfo.endCursor) {
          return undefined;
        }

        return { cursor: pageInfo.endCursor };
      },
      placeholderData: keepPreviousData,
    });

  const allProducts =
    data?.pages.flatMap((page) => page.collection?.products.nodes ?? []) ?? [];

  // Client-side filtering: exclude the heel spur insole and apply product-type
  // filter. The Shopify Storefront API silently ignores the `productType`
  // ProductFilter for this automated collection (the collection's `filters`
  // response does not include a product_type facet), so we filter here.
  const products = allProducts.filter((p) => {
    if (p.id.includes(EXCLUDED_PRODUCT_ID)) return false;
    if (filters.type.length > 0 && !filters.type.includes(p.productType)) {
      return false;
    }
    return true;
  });

  // Extract available filter options from the first page for the sidebar
  const availableFilters = data?.pages[0]?.collection?.products?.filters ?? [];

  // Fetch all product types across the full catalog in a single request.
  // This replaces the previous approach of deriving types from paginated
  // product data (allProducts), which was incomplete until all pages were loaded.
  const { data: productTypesData } = useGetProductTypesQuery(
    { first: 250 },
    { staleTime: 1000 * 60 * 60 }, // 1 hour — product types rarely change
  );
  const availableProductTypes =
    productTypesData?.productTypes.edges.map((e) => e.node).filter(Boolean) ??
    [];

  const observedProduct =
    products[Math.max(products.length - OBSERVED_ITEMS_FROM_END, 0)];

  useEffect(() => {
    if (!observedProduct?.handle || !hasNextPage || isFetchingNextPage) {
      return;
    }

    const candidates = Array.from(
      document.querySelectorAll<HTMLElement>("[data-product-handle]"),
    );

    const target = candidates.find(
      (node) => node.dataset.productHandle === observedProduct.handle,
    );

    if (!target) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (entry.isIntersecting) {
          return fetchNextPage();
        }
      },
      {
        root: null,
        threshold: 0,
      },
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, observedProduct]);

  return (
    <div className="min-h-screen px-4 pt-8 pb-24 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <PageBreadcrumb
          className="mb-8"
          segments={[
            {
              label: t("SingleProductPage.breadcrumbs.home"),
              to: "/$locale",
              params: { locale },
            },
            { label: t("ProductsPage.title") },
          ]}
        />

        <div className="mb-10 max-mobile:mb-6">
          <h1 className="text-3xlarge leading-tight font-bold tracking-[-0.02em] text-text-1 max-mobile:text-xlarge">
            {t("ProductsPage.title")}
          </h1>
          <p className="mt-2 text-small text-text-3 tabular-nums">
            {t("ProductsPage.count", { count: products.length })}
          </p>
        </div>

        {/* Mobile-only controls: filter drawer trigger */}
        <div className="mb-4 flex flex-wrap items-center gap-3 lg:hidden">
          {/* Mobile filter trigger */}
          <Button
            dark="outline"
            size="small"
            onClick={() => setDrawerOpen(true)}
            aria-label={t("Filters.mobile.title")}
          >
            <SlidersHorizontal className="size-4" aria-hidden="true" />
            <span>{t("Filters.filter")}</span>
            {activeFilterCount > 0 && (
              <Badge
                variant="primary"
                className="size-5 justify-center p-0 text-xs"
              >
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </div>

        <div className="flex gap-8">
          <FilterSidebar
            productFilters={filterState}
            sortKey={sortKey}
            onSortChange={setSortKey}
            availableFilters={availableFilters}
            availableProductTypes={availableProductTypes}
          />

          <div className="min-w-0 flex-1">
            {/* Mobile-only: show active pills above products (desktop pills are inside FilterSidebar) */}
            {isFiltered && (
              <div className="lg:hidden">
                <ActiveFilterPills productFilters={filterState} />
              </div>
            )}
            {products.length > 0 ? (
              <>
                <ProductGrid products={products} locale={locale} columns={4} />

                {isFetchingNextPage && (
                  <div className="mt-12 text-center">
                    <Loader2Icon
                      className="mx-auto mb-4 animate-spin"
                      aria-hidden="true"
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="rounded-3xl bg-white px-6 py-20 text-center">
                <PackageSearch
                  className="text-text-5 mx-auto mb-4 size-12"
                  aria-hidden="true"
                />
                <h2 className="text-xlarge text-text-1 mb-2 font-bold">
                  {t("ProductsPage.emptyTitle")}
                </h2>
                <p className="text-text-3">
                  {isFiltered
                    ? t("Filters.noResults")
                    : t("ProductsPage.emptySubtitle")}
                </p>
                {isFiltered && (
                  <p className="text-text-4 mt-1 text-sm">
                    {t("Filters.noResultsHint")}
                  </p>
                )}
                {isFiltered && (
                  <Button
                    primary="ghost"
                    size="extraSmall"
                    className="mt-4"
                    onClick={filterState.resetFilters}
                  >
                    {t("Filters.resetFilters")}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mobile filter drawer */}
        <MobileFilterDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          productFilters={filterState}
          resultCount={products.length}
          sortKey={sortKey}
          onSortChange={setSortKey}
          availableFilters={availableFilters}
          availableProductTypes={availableProductTypes}
        />
      </div>
    </div>
  );
}
